// ============================================================================
// Utilidad — Borradores de formulario.
//
// Persiste el progreso de un formulario (objeto plano serializable) en
// localStorage mientras el usuario lo completa, para que una recarga, un
// cambio de ruta o un re-render no borren lo que estaba escribiendo.
//
// Diseño deliberadamente independiente de la app y de las vistas:
//   - No conoce qué campos tiene cada formulario: recibe/entrega objetos
//     planos (JSON). El llamador decide el shape.
//   - No conoce empresa/usuario: la CLAVE la construye el llamador con
//     `crearClaveBorrador(...)`, de modo que dos usuarios o dos empresas en
//     el mismo navegador no compartan borradores.
//   - El autoguardado se apoya en el `debounce` compartido.
//
// Por qué localStorage y no IndexedDB: los borradores son texto/números/
// booleanos/ids. Nunca se guardan objetos `File` — los archivos (imágenes)
// se suben al servidor al seleccionarlos, así que no queda estado binario
// local que persistir. Si algún día un formulario mantuviera `File` sin
// subir, ese caso sí justificaría IndexedDB; no es el caso hoy.
// ============================================================================

import { debounce } from './temporizadores.js';

const PREFIJO = 'pf:borrador:';

/** Acceso tolerante a localStorage (SSR/tests/modo privado). */
function almacen() {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * Construye una clave namespaced y estable para un borrador.
 *
 * @param {string} ambito - tipo de formulario (ej. 'formulario-producto').
 * @param {string} [contexto] - aislamiento (empresa/usuario o id de entidad).
 * @param {string} [sufijo] - discriminador extra (ej. 'editar:<id>').
 * @returns {string}
 */
export function crearClaveBorrador(ambito, contexto, sufijo) {
  const partes = [ambito, contexto, sufijo].filter(
    (parte) => parte !== undefined && parte !== null && parte !== '',
  );
  return `${PREFIJO}${partes.join(':')}`;
}

/**
 * @param {string} clave
 * @param {object} datos - objeto plano serializable a JSON.
 * @returns {boolean} true si se persistió.
 */
export function guardarBorrador(clave, datos) {
  const store = almacen();
  if (!store) return false;
  try {
    store.setItem(clave, JSON.stringify({ datos, actualizadoEn: new Date().toISOString() }));
    return true;
  } catch {
    // Cuota llena o modo privado: no es recuperable desde acá.
    return false;
  }
}

/**
 * @param {string} clave
 * @returns {{ datos: object, actualizadoEn: string | null } | null}
 */
export function leerBorrador(clave) {
  const store = almacen();
  if (!store) return null;
  try {
    const bruto = store.getItem(clave);
    if (!bruto) return null;
    const sobre = JSON.parse(bruto);
    if (!sobre || typeof sobre !== 'object' || !sobre.datos || typeof sobre.datos !== 'object') {
      return null;
    }
    return { datos: sobre.datos, actualizadoEn: sobre.actualizadoEn ?? null };
  } catch {
    // JSON corrupto: se trata como ausencia de borrador.
    return null;
  }
}

/** @param {string} clave */
export function eliminarBorrador(clave) {
  const store = almacen();
  if (!store) return;
  try {
    store.removeItem(clave);
  } catch {
    // Nada que limpiar si el almacenamiento no está disponible.
  }
}

/**
 * Gestor de autoguardado con estado. Envuelve guardar/leer/limpiar y agrega
 * un guardado diferido que se puede cancelar (evita que un guardado pendiente
 * "reviva" un borrador que ya se limpió).
 *
 * @param {object} opciones
 * @param {string} opciones.clave
 * @param {number} [opciones.esperaMs] - retardo del autoguardado (default 600).
 * @param {(estado: 'guardando' | 'guardado' | 'error') => void} [opciones.alEstado]
 */
export function crearGestorBorrador({ clave, esperaMs = 600, alEstado } = {}) {
  // Cada operación (programar/volcar/limpiar) sube `version`. El guardado
  // diferido lleva la versión con la que se programó y se descarta si ya no
  // es la vigente — así un temporizador obsoleto no escribe sobre un borrador
  // ya guardado o descartado.
  let version = 0;

  const autoguardar = debounce((datos, v) => {
    if (v !== version) return;
    const ok = guardarBorrador(clave, datos);
    alEstado?.(ok ? 'guardado' : 'error');
  }, esperaMs);

  return {
    clave,
    /** Programa un guardado diferido (uso: en cada input/change). */
    programar(datos) {
      version += 1;
      alEstado?.('guardando');
      autoguardar(datos, version);
    },
    /** Guarda de inmediato y descarta cualquier guardado diferido pendiente. */
    volcar(datos) {
      version += 1;
      const ok = guardarBorrador(clave, datos);
      alEstado?.(ok ? 'guardado' : 'error');
      return ok;
    },
    /** @returns {ReturnType<typeof leerBorrador>} */
    leer() {
      return leerBorrador(clave);
    },
    /** Descarta el borrador y cancela guardados pendientes. */
    limpiar() {
      version += 1;
      eliminarBorrador(clave);
    },
  };
}
